import { useState, useEffect, useId, useRef } from 'react';
import { Menu, Pin, PinOff } from 'lucide-react';
import { useT } from '../../i18n';
import { NavMenuItems } from './NavMenuItems';

export interface NavMenuProps {
  /** Currently active view id (drives the active-item styling). */
  currentView: string;
  /** Navigate to the given view id. */
  onNavigate: (id: string) => void;
  /** Whether the menu is pinned to the left as a sidebar. */
  pinned?: boolean;
  /** Toggle the pinned state (optionally forcing a value). */
  onTogglePin?: (value?: boolean) => void;
  /** Badge count shown on the archive item. */
  archiveCount?: number;
  /** The live Padel24Engine wiring object; forwarded to NavMenuItems for the version footer. */
  Padel24Engine: any;
}

export function NavMenu({
  currentView,
  onNavigate,
  pinned,
  onTogglePin,
  archiveCount = 0,
  Padel24Engine,
}: NavMenuProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  // Ref to the entire NavMenu container (burger button + menu popup). Used to
  // detect clicks outside — more reliable than a fixed backdrop, which can be
  // blocked by other elements or modals.
  const containerRef = useRef(null);
  // ── A NAMED, STATEFUL TOGGLE (#1144, WCAG 4.1.2) ────────────────────
  // The burger had only a `title`, and nothing said whether the list was open
  // or which element it opened. `aria-expanded` and `aria-controls` say both.
  // The popover is a list of navigation buttons, NOT an ARIA `menu`: that role
  // promises arrow-key navigation this component does not implement.
  const toggleRef = useRef<HTMLButtonElement>(null);
  const popoverId = useId();

  // Load the saved pin state on startup
  useEffect(() => {
    try {
      const saved = localStorage.getItem('padel24_nav_pinned');
      if (saved === 'true' && !pinned) onTogglePin?.(true);
    } catch (_e) {
      /* ignore */
    } // deviation: unused catch binding e -> _e for lint (@typescript-eslint/no-unused-vars)
    // deviation: verbatim mount-once effect (loads saved pin pref); deps intentionally []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the menu on a click or touch outside. Also on the Escape key for
  // keyboard users. The listener is registered only when the menu is open, and
  // uses the capture phase so we catch clicks before other handlers stop them.
  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        // Focus returns to what opened it, not to the page's start (2.4.3).
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('touchstart', handleClickOutside, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('touchstart', handleClickOutside, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleTogglePin = () => {
    const newValue = !pinned;
    onTogglePin?.(newValue);
    try {
      localStorage.setItem('padel24_nav_pinned', String(newValue));
    } catch (_e) {
      /* ignore */
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={toggleRef}
        onClick={() => setOpen((o) => !o)}
        className="p-2 rounded-lg hover:bg-bg-soft transition-colors flex items-center justify-center"
        title={t('ui.nav.menu')}
        aria-label={t('ui.nav.menu')}
        aria-expanded={open}
        aria-controls={popoverId}
      >
        <Menu size={18} strokeWidth={2} />
      </button>

      {open && (
        <div
          id={popoverId}
          className="absolute top-full left-0 mt-1 w-60 card-elev py-1.5 px-1.5"
          style={{ zIndex: 50 }}
        >
          <NavMenuItems
            currentView={currentView}
            onNavigate={onNavigate}
            Padel24Engine={Padel24Engine}
            onItemClick={() => setOpen(false)}
            archiveCount={archiveCount}
            headerSlot={
              <button
                onClick={handleTogglePin}
                className="p-1.5 rounded hover:bg-bg-soft transition-colors text-ink-3 hover:text-ink-2"
                title={pinned ? t('ui.nav.unpin') : t('ui.nav.pinLeft')}
                aria-label={pinned ? t('ui.nav.unpin') : t('ui.nav.pinLeft')}
              >
                {pinned ? <PinOff size={14} /> : <Pin size={14} />}
              </button>
            }
          />
        </div>
      )}
    </div>
  );
}
